import { useCallback, useEffect } from 'react';

import {
    useGetPublicPartnerData,
    useNasaImageOfDay,
} from '@/v5/@hooks/auth/login/';

/**
 * Custom hook to set background of auth pages and fake login page (partner branding)
 * @param {import('react').MutableRefObject<T>} ref - ref of background component that is reassigned based on partner data
 * @param {{backgroundType: number; backgroundImage: string; backgroundColor: string } | Object} [backgroundOptions] - overriden options that can be passed in from partner data at `/theme/{hostHeader}` endpoint
 * */
function usePartnerProvidedBackground(ref, backgroundOptions = {}) {
    const { data, isLoading } = useGetPublicPartnerData();

    // Only enable NASA query when needed
    const shouldFetchNasa =
        backgroundOptions?.backgroundType === 4 ||
        (data?.backgroundType === 4 && !backgroundOptions?.backgroundType);

    const { data: nasaData, isLoading: nasaLoading } =
        useNasaImageOfDay(shouldFetchNasa);

    const renderPartnerProvidedBackground = useCallback(
        (partnerData, nasaImageData = null) => {
            if (!ref?.current) return;

            switch (partnerData.backgroundType) {
                // Default bg color
                case 0: {
                    ref.current.style.backgroundColor = '#F5F5F5';
                    ref.current.style.backgroundImage = 'none';
                    break;
                }

                // Solid defined bg color
                case 1: {
                    let bgColor;
                    bgColor = partnerData.backgroundColor.includes('#')
                        ? partnerData.backgroundColor
                        : `#${partnerData.backgroundColor}`;
                    ref.current.style.backgroundColor = bgColor;
                    ref.current.style.backgroundImage = 'none';
                    break;
                }

                // Defined bg image
                case 2: {
                    let imagePath =
                        import.meta.env.VITE_APP_IMAGE_BASE +
                        partnerData?.backgroundImage;
                    ref.current.style.backgroundPosition = 'center';
                    ref.current.style.backgroundRepeat = 'no-repeat';
                    ref.current.style.backgroundSize = 'cover';
                    ref.current.style.backgroundImage = `url('${imagePath}')`;
                    break;
                }

                // Bing image of the day
                case 3: {
                    ref.current.style.backgroundPosition = 'center';
                    ref.current.style.backgroundRepeat = 'no-repeat';
                    ref.current.style.backgroundSize = 'cover';
                    ref.current.style.backgroundImage =
                        'url(\'https://bing.biturl.top/?resolution=1920&format=image&index=0\')';
                    break;
                }

                // NASA image of the day - now using cached data
                case 4: {
                    if (nasaImageData) {
                        const { hdurl, thumbnail_url } = nasaImageData;
                        ref.current.style.backgroundPosition = 'center';
                        ref.current.style.backgroundRepeat = 'no-repeat';
                        ref.current.style.backgroundSize = 'cover';
                        ref.current.style.backgroundImage = `url('${hdurl || thumbnail_url}')`;
                    }
                    break;
                }

                default:
                    ref.current.style.backgroundColor = '#F5F5F5';
                    ref.current.style.backgroundImage = 'none';
            }
        },
        [ref],
    );

    useEffect(() => {
        // Using background overriden options
        if (backgroundOptions && Object.keys(backgroundOptions).length > 0) {
            renderPartnerProvidedBackground(backgroundOptions, nasaData);
        } else if (data) {
            // Using data from `/theme/{hostHeader}`
            renderPartnerProvidedBackground(data, nasaData);
        }
    }, [data, backgroundOptions, nasaData, renderPartnerProvidedBackground]);

    return {
        data,
        isLoading: isLoading || (shouldFetchNasa && nasaLoading),
    };
}

export { usePartnerProvidedBackground };
